/**
 * The bot — the thin layer that binds prediction, decision and mechanics to a
 * car, and the director that runs a whole roster of them.
 *
 * ===========================================================================
 * WHAT LIVES HERE AND WHAT DOES NOT
 * ===========================================================================
 *
 * Everything below this file is stateless. `intents.ts` scores a context;
 * `mechanics.ts` turns a target into a `CarInput`. All of a bot's MEMORY is
 * here, and it is deliberately short:
 *
 *   - the current intent and the tick it was adopted (hysteresis),
 *   - the aim bias for the current shot,
 *   - a ring buffer of pending inputs (reaction delay).
 *
 * That is the entire mutable surface, which is what makes the determinism test
 * meaningful: two directors built from the same seeds and fed the same states
 * produce bit-identical input sequences because there is nothing else to
 * diverge.
 *
 * ===========================================================================
 * NO THREE.JS, NO DOM, NO WORLD
 * ===========================================================================
 *
 * Bots run headless — in tests, in a training loop, on a server. They never
 * import the renderer, and they never import `World`: they consume
 * `BotWorldView`, a structural read-only interface that a `World` satisfies via
 * `viewOf`. The bot layer therefore cannot accidentally step the simulation,
 * and it keeps compiling while the world is refactored underneath it.
 *
 * (./prediction imports two pure symbols from src/sim/world — the ball-vs-arena
 * resolver and its pass count — precisely so that the predicted ball and the
 * simulated one cannot drift apart. It still cannot reach the world itself.)
 *
 * ===========================================================================
 * REACTION DELAY IS A QUEUE, NOT A SLEEP
 * ===========================================================================
 *
 * A bot decides on the CURRENT world every tick and then emits that decision
 * `reactionTicks` later. It is not "decide every N ticks" — that produces a bot
 * which is perfectly sharp N-1 ticks out of N and blind on the Nth. A queue
 * produces one that is smoothly, consistently late, which is what a human is.
 *
 * ===========================================================================
 * THE LEARNED-POLICY SEAM
 * ===========================================================================
 *
 * `Policy` is the whole interface: `observe(context) -> CarInput`. The scripted
 * bot is one implementation. A trained network would be another, constructed by
 * the caller and passed as `BotConfig.policy`; nothing else changes, because
 * the director already owns prediction, roles and the input queue.
 *
 * No such policy ships here. Selecting the `'learned'` tier without supplying
 * one throws `LearnedPolicyUnavailableError` — see the note in ./difficulty for
 * why that is better than falling back to a scripted bot.
 */

import { BALL_SPAWN } from '../physics/arena-data';
import type { BallState } from '../physics/ball';
import { neutralInput, type CarInput, type CarState } from '../physics/car-state';
import { BALL_RADIUS } from '../physics/constants';
import { clamp, dist, length, vec3, type Vec3 } from '../physics/math';
import type { ResolvedPhysics } from '../physics/mutators';
import type { ArenaGeometry } from '../physics/surface';
import {
  difficultyParams,
  makeRng,
  symmetric,
  LearnedPolicyUnavailableError,
  type DifficultyParams,
  type DifficultyTier,
  type Rng,
} from './difficulty';
import {
  aerialHit,
  driveToPoint,
  groundShot,
  needsRecovery,
  onNonFloorSurface,
  recover,
  runKickoff,
  sanitizeInput,
  unstick,
  wallRead,
  UNSTICK_TICKS,
  WEDGE_SPEED,
  WEDGE_THROTTLE,
  WEDGE_TICKS,
  type KickoffVariant,
} from './mechanics';
import {
  assignRoles,
  attackingGoalCenter,
  estimateBallReach,
  estimateTimeToReach,
  goalThreat,
  chooseIntent,
  intentTarget,
  nearestBoostTarget,
  ownGoalCenter,
  GROUND_REACH_MAX_Z,
  type BallReach,
  type Intent,
  type IntentContext,
  type IntentDecision,
  type Role,
} from './intents';
import {
  BallPredictor,
  makeSample,
  type BallPrediction,
  type BallPredictionEvents,
} from './prediction';

// ---------------------------------------------------------------------------
// The world, as a bot sees it
// ---------------------------------------------------------------------------

/**
 * Read-only view of the simulation. Structural on purpose: a `World` satisfies
 * it through `viewOf`, and a test can satisfy it with an object literal.
 */
export interface BotWorldView {
  tick: number;
  ball: BallState;
  cars: readonly CarState[];
  arena: ArenaGeometry;
  /** Is boost pad `index` (RLBot FieldInfo order) collectable right now? */
  padActive(index: number): boolean;
  /** True while the field is held at the kickoff pose. */
  frozen: boolean;
}

/**
 * Adapt anything world-shaped into a `BotWorldView` without importing it.
 *
 * `tick` and `frozen` are GETTERS, not copies: the view is built once and read
 * every tick, and a snapshot taken at construction would freeze the bot at tick
 * zero forever. The object references (ball, cars, arena) are stable across a
 * world's lifetime, so those are plain fields.
 */
export const viewOf = (source: {
  tick: number;
  ball: BallState;
  cars: readonly CarState[];
  arena: ArenaGeometry;
  padActive(index: number): boolean;
  match: { frozen: boolean };
}): BotWorldView => ({
  get tick() {
    return source.tick;
  },
  ball: source.ball,
  cars: source.cars,
  arena: source.arena,
  padActive: (index: number) => source.padActive(index),
  get frozen() {
    return source.match.frozen;
  },
});

// ---------------------------------------------------------------------------
// Policy
// ---------------------------------------------------------------------------

/** Everything a policy is given. Assembled by the director once per bot tick. */
export interface PolicyContext extends IntentContext {
  view: BotWorldView;
  prediction: BallPrediction;
  rng: Rng;
  /** Ticks since the kickoff went live, or -1 when this is not a kickoff. */
  kickoffTicks: number;
}

/**
 * The seam a learned policy plugs into. One method, one direction of data
 * flow, no access to the simulation beyond the context it is handed.
 */
export interface Policy {
  readonly name: string;
  /** Controls for this tick. Must return axes in [-1, 1]; the director
   *  sanitizes anyway, because a policy that returns NaN must not be able to
   *  poison the physics. */
  observe(context: PolicyContext): CarInput;
  /** Drop all per-episode memory (kickoff, respawn, new match). */
  reset(): void;
}

// ---------------------------------------------------------------------------
// The scripted policy
// ---------------------------------------------------------------------------

/**
 * Ball speed below which, at the spawn point, this is a kickoff.
 *
 * NOT zero, and not near it. A ball "at rest" on the floor is in a permanent
 * one-tick cycle — gravity gives it -5.4 uu/s, the contact bounces that back at
 * 0.6 — so its speed hovers a few uu/s above zero forever. A tight threshold
 * here silently disables every kickoff mechanic, which is a failure that looks
 * like the bot merely driving at the ball, and so does not announce itself.
 * 100 uu/s is two orders of magnitude below any real touch.
 */
const KICKOFF_BALL_SPEED = 100;
/** How far the ball may be from its spawn and still count as a kickoff. */
const KICKOFF_BALL_RADIUS = 50;
/** Kickoff behaviour is abandoned after this long regardless. */
const KICKOFF_MAX_TICKS = 360;

/** Aim point for a clearance: up-field and toward the nearer sideline, which
 *  is the safest place to put a ball you cannot shoot. */
const clearanceAim = (team: 0 | 1, from: Vec3, out: Vec3 = vec3()): Vec3 => {
  out.x = clamp(from.x * 2, -3000, 3000);
  out.y = team === 0 ? 1500 : -1500;
  out.z = 0;
  return out;
};

export interface ScriptedBotDebug {
  intent: Intent;
  role: Role;
  reachTime: number;
  kickoff: boolean;
  /** Ticks of input lag currently applied. */
  lag: number;
}

/**
 * The default `Policy`: utility decision, then a mechanic, then the reaction
 * queue.
 */
export class ScriptedPolicy implements Policy {
  readonly name = 'scripted';

  private decision: IntentDecision | null = null;
  /** Aim bias for the current shot, redrawn whenever the intent changes. */
  private aimError = 0;
  /** Pending inputs, oldest first. Length is bounded by `lag`. */
  private readonly queue: CarInput[] = [];
  private lag = 0;
  private debugInfo: ScriptedBotDebug | null = null;
  /** Consecutive ticks spent asking for throttle and not moving. */
  private wedged = 0;
  /** Ticks of `unstick` still owed. */
  private unsticking = 0;
  /** Tick of the previous call, so a gap (a demolition) clears the counters
   *  instead of carrying a stale wedge across a respawn. */
  private lastTick = -1;

  constructor(readonly params: DifficultyParams) {
    this.lag = params.reactionTicks;
  }

  reset(): void {
    this.decision = null;
    this.queue.length = 0;
    this.aimError = 0;
    this.lag = this.params.reactionTicks;
    this.debugInfo = null;
    this.wedged = 0;
    this.unsticking = 0;
    this.lastTick = -1;
  }

  debug(): ScriptedBotDebug | null {
    return this.debugInfo;
  }

  observe(ctx: PolicyContext): CarInput {
    const fresh = this.decide(ctx);
    this.queue.push(fresh);

    // Emit the oldest pending input once the queue is longer than the lag. The
    // loop (rather than a single shift) matters when the jitter has just
    // SHORTENED the lag: the queue drains to the new length in one tick instead
    // of the bot silently running on a stale backlog.
    let emitted: CarInput | null = null;
    while (this.queue.length > this.lag) emitted = this.queue.shift() as CarInput;
    return emitted ?? neutralInput();
  }

  /** The undelayed decision for this tick. */
  private decide(ctx: PolicyContext): CarInput {
    const contiguous = ctx.tick === this.lastTick + 1;
    this.lastTick = ctx.tick;
    if (!contiguous) {
      this.wedged = 0;
      this.unsticking = 0;
    }

    if (needsRecovery(ctx.car)) return this.escapeWedge(ctx, recover(ctx.car));

    if (ctx.kickoffTicks >= 0) {
      const variant: KickoffVariant =
        ctx.role !== 'first'
          ? 'cheat-back'
          : this.params.mechanics.speedFlip
            ? 'speed-flip'
            : 'fast-diagonal';
      this.debugInfo = {
        intent: 'goForBall',
        role: ctx.role,
        reachTime: ctx.reach.time,
        kickoff: true,
        lag: this.lag,
      };
      this.wedged = 0;
      this.unsticking = 0;
      return runKickoff(variant, ctx.car, ctx.team, ctx.view.ball.position, {
        elapsedTicks: ctx.kickoffTicks,
        throttleCap: this.params.throttleCap,
        allowBoost: true,
        allowPowerslide: this.params.mechanics.powerslide,
      });
    }

    const previous = this.decision;
    const decision = chooseIntent(ctx, previous);
    if (previous === null || decision.sinceTick !== previous.sinceTick) {
      // A new intent is a new shot: redraw the aim bias and the reaction lag.
      this.aimError = symmetric(ctx.rng, this.params.aimError);
      this.lag =
        this.params.reactionTicks +
        Math.floor(ctx.rng.next() * (this.params.reactionJitterTicks + 1));
    }
    this.decision = decision;

    this.debugInfo = {
      intent: decision.intent,
      role: ctx.role,
      reachTime: ctx.reach.time,
      kickoff: false,
      lag: this.lag,
    };

    return this.escapeWedge(ctx, this.execute(decision.intent, ctx));
  }

  /**
   * Override the tick's input when the car is wedged.
   *
   * The decision layer has no idea where the walls are, and the drive controller
   * assumes that asking for throttle produces motion. Neither is wrong on its
   * own, and together they will hold a car nose-first into the inside corner of
   * its own net at 8 uu/s under full throttle until the match ends — the
   * decision is re-scored every tick and comes out the same every tick, because
   * nothing in the context changes while the car is not moving.
   *
   * So the escape is keyed on the ONE observation neither layer makes: the bot
   * asked for motion and did not get any. That is deliberately mechanism-blind.
   * It does not know what the car is caught on and does not need to, which is
   * what makes it a bound on how long a bot can be stuck rather than a fix for
   * one shape of obstacle.
   *
   * Frozen ticks do not count: a frozen car is not moving because the world is
   * holding it. AIRBORNE TICKS DO, and deliberately — a car in free flight is
   * never below WEDGE_SPEED for half a second, because gravity alone has it
   * doing 495 uu/s by then, so "airborne and slow, for this long" does not mean
   * flying. It means resting on the body rather than the wheels, which reports
   * zero wheel contacts, gets no engine force, and is the pose a car ends up in
   * when it lands on its roof. `unstick` reaches for boost there.
   */
  private escapeWedge(ctx: PolicyContext, input: CarInput): CarInput {
    if (this.unsticking > 0) {
      this.unsticking--;
      return unstick(ctx.car, ctx.reach.position);
    }

    const asking = Math.abs(input.throttle) > WEDGE_THROTTLE || input.boost;
    if (ctx.view.frozen || !asking || length(ctx.car.velocity) >= WEDGE_SPEED) {
      this.wedged = 0;
      return input;
    }

    if (++this.wedged < WEDGE_TICKS) return input;
    this.wedged = 0;
    this.unsticking = UNSTICK_TICKS;
    return unstick(ctx.car, ctx.reach.position);
  }

  private execute(intent: Intent, ctx: PolicyContext): CarInput {
    const p = this.params;
    const drive = {
      throttleCap: p.throttleCap,
      allowBoost: p.useBoostForSpeed,
      allowPowerslide: p.mechanics.powerslide,
      boostReserve: p.boostReserve,
    };

    switch (intent) {
      case 'goForBall':
      case 'challenge':
      case 'save':
      case 'clear': {
        const contact = ctx.reach.position;

        if (contact.z > GROUND_REACH_MAX_Z) {
          if (p.mechanics.aerials && contact.z >= p.aerialMinHeight) {
            return aerialHit(ctx.car, contact, {
              allowBoost: true,
              boostReserve: 0,
              allowDoubleJump: p.mechanics.doubleJump,
              // `reach.time` is when the BALL is at `contact`, which is exactly
              // the deadline the takeoff and the second jump are timed against.
              arrivalTime: ctx.reach.time,
            });
          }
          if (p.mechanics.wallReads) {
            return wallRead(ctx.car, ctx.prediction, drive);
          }
        }

        const aim =
          intent === 'goForBall' || intent === 'challenge'
            ? ctx.attackingGoal
            : clearanceAim(ctx.team, contact);

        return groundShot(ctx.car, contact, aim, {
          ...drive,
          // A save is worth the whole tank; nothing else is.
          boostReserve: intent === 'save' ? 0 : p.boostReserve,
          aimError: this.aimError,
          allowFlip: p.mechanics.kickoffFlip,
        });
      }

      case 'demo': {
        if (ctx.nearestOpponent === null) break;
        const victim = ctx.view.cars[ctx.nearestOpponent.carIndex];
        // Lead the target by a fifth of a second — enough to matter at
        // supersonic, short enough that a swerve is not rewarded.
        const lead = vec3(
          victim.position.x + victim.velocity.x * 0.2,
          victim.position.y + victim.velocity.y * 0.2,
          ctx.car.position.z
        );
        return driveToPoint(ctx.car, lead, { ...drive, allowBoost: true, boostReserve: 0 });
      }

      case 'collectBoost':
      case 'shadowDefend':
      case 'rotateBackPost':
        break;
    }

    const target = intentTarget(intent, ctx);
    // Pin the target to the car's own height only while the car is on the
    // FLOOR. On a wall or a ramp that height is a position ON the surface, so
    // lifting a floor target to it reads as "hold where you are", and the drive
    // controller holds it. Left on the floor the same target points the
    // controller down the surface, which is how a car comes off one — see
    // `onNonFloorSurface` in ./mechanics.
    if (!onNonFloorSurface(ctx.car)) target.z = ctx.car.position.z;
    return driveToPoint(ctx.car, target, {
      ...drive,
      stopAtTarget: intent === 'rotateBackPost',
      arriveTolerance: intent === 'rotateBackPost' ? 200 : 60,
    });
  }
}

// ---------------------------------------------------------------------------
// Director
// ---------------------------------------------------------------------------

export interface BotConfig {
  /** Index into `BotWorldView.cars`. */
  carIndex: number;
  difficulty: DifficultyTier;
  /** Seed for this bot's generator. Defaults to `carIndex`, so a roster built
   *  without explicit seeds is still reproducible — and still varied. */
  seed?: number;
  /** A policy to use instead of the scripted one. This is how a learned policy
   *  is installed. */
  policy?: Policy;
  /** Override the tier's parameters. Tests use this; gameplay should not. */
  params?: DifficultyParams;
}

interface BotSlot {
  config: BotConfig;
  params: DifficultyParams;
  policy: Policy;
  seed: number;
  rng: Rng;
}

/**
 * Build the policy for a config. Throws for the `'learned'` tier unless the
 * caller supplied an actual policy — see ./difficulty.
 */
export const createPolicy = (config: BotConfig, params: DifficultyParams): Policy =>
  config.policy ?? new ScriptedPolicy(params);

export interface BotDirectorOptions {
  /** Prediction horizon in ticks. Defaults to the strongest tier's. */
  horizonTicks?: number;
  /**
   * The world's resolved physics, forwarded to the predictor. Pass
   * `world.physics`: under a ball-size or bounciness mutator, predicting with
   * the defaults aims every bot at a ball that is not the one in play.
   */
  physics?: ResolvedPhysics;
}

/**
 * Runs a roster of bots against one world.
 *
 * Owns the two things that must NOT be per-bot: the ball prediction (computed
 * at most once per tick and shared) and the role assignment (which is a
 * property of a team, not of a car).
 */
export class BotDirector {
  readonly predictor: BallPredictor;
  private readonly slots: BotSlot[] = [];
  /** Ticks since the kickoff went live; -1 while frozen or once play is on. */
  private kickoffTicks = -1;
  private wasFrozen = true;

  private readonly sample = makeSample();
  /** Live `position` references, rebuilt only when the roster size changes.
   *  Shared by every context this tick rather than rebuilt per bot. */
  private carPositions: Vec3[] = [];

  constructor(
    arena: ArenaGeometry,
    configs: readonly BotConfig[],
    options: BotDirectorOptions = {}
  ) {
    let horizon = options.horizonTicks ?? 0;

    for (const config of configs) {
      if (config.policy === undefined && config.difficulty === 'learned' && config.params === undefined) {
        throw new LearnedPolicyUnavailableError();
      }
      const params =
        config.params ??
        (config.difficulty === 'learned'
          ? difficultyParams('allstar')
          : difficultyParams(config.difficulty));
      horizon = Math.max(horizon, params.predictionHorizonTicks);
      const seed = config.seed ?? config.carIndex;
      this.slots.push({
        config,
        params,
        policy: createPolicy(config, params),
        seed,
        rng: makeRng(seed),
      });
    }

    this.predictor = new BallPredictor(arena, {
      horizonTicks: Math.max(2, horizon),
      physics: options.physics,
    });
  }

  /** Car indices this director drives, in configuration order. */
  get carIndices(): number[] {
    return this.slots.map((slot) => slot.config.carIndex);
  }

  /** Feed the previous step's events. Drives prediction invalidation. */
  observe(events: BallPredictionEvents): void {
    this.predictor.observe(events);
  }

  /**
   * Drop all per-episode memory: intents, input queues, the prediction cache
   * and the generators.
   *
   * The generators are rewound to their configured seeds on purpose. A caller
   * resets on a kickoff, and a kickoff is the start of a new episode: two
   * matches played from the same seed must produce the same bots, which they
   * would not if the aim-error stream carried over from however many shots the
   * previous match happened to take.
   */
  reset(): void {
    for (const slot of this.slots) {
      slot.policy.reset();
      slot.rng = makeRng(slot.seed);
    }
    this.predictor.invalidate();
    this.kickoffTicks = -1;
    this.wasFrozen = true;
  }

  /** Debug record for a car, or null when it is not bot-driven or has not run. */
  debug(carIndex: number): ScriptedBotDebug | null {
    const slot = this.slots.find((s) => s.config.carIndex === carIndex);
    if (slot === undefined) return null;
    const policy = slot.policy;
    return policy instanceof ScriptedPolicy ? policy.debug() : null;
  }

  /**
   * One input per bot, in configuration order. The caller places them into its
   * own input array by `carIndex`; `inputsFor` does that in one call.
   */
  step(view: BotWorldView): CarInput[] {
    this.trackKickoff(view);
    this.refreshCarPositions(view);
    const prediction = this.predictor.predict(view.ball, view.tick);

    // --- reach times for every car, so roles and race comparisons agree ------
    const reaches = this.computeReaches(view, prediction);
    const roles = this.computeRoles(view, reaches);

    const kickoff = this.isKickoff(view) ? this.kickoffTicks : -1;
    const threat: [number, number] = [
      goalThreat(prediction, 0),
      goalThreat(prediction, 1),
    ];

    const out: CarInput[] = [];
    for (const slot of this.slots) {
      const carIndex = slot.config.carIndex;
      const car = view.cars[carIndex];
      if (car === undefined || car.demolished) {
        out.push(neutralInput());
        continue;
      }

      const ctx = this.buildContext(
        view,
        prediction,
        slot,
        car,
        reaches,
        roles.get(carIndex) ?? 'third',
        threat[car.team],
        kickoff
      );
      out.push(sanitizeInput(slot.policy.observe(ctx)));
    }
    return out;
  }

  /** Inputs laid out parallel to `view.cars`, with neutral for every car this
   *  director does not drive — the shape `World.step` wants. */
  inputsFor(view: BotWorldView): CarInput[] {
    const inputs = view.cars.map(() => neutralInput());
    const produced = this.step(view);
    for (let i = 0; i < this.slots.length; i++) {
      const index = this.slots[i].config.carIndex;
      if (index >= 0 && index < inputs.length) inputs[index] = produced[i];
    }
    return inputs;
  }

  // -------------------------------------------------------------------------

  /**
   * `CarState.position` is REPLACED, not mutated, by kickoff resets, so the
   * cached references have to be refreshed every tick. The array itself is
   * reused; only its slots are rewritten.
   */
  private refreshCarPositions(view: BotWorldView): void {
    if (this.carPositions.length !== view.cars.length) {
      this.carPositions = view.cars.map((car) => car.position);
      return;
    }
    for (let i = 0; i < view.cars.length; i++) this.carPositions[i] = view.cars[i].position;
  }

  private trackKickoff(view: BotWorldView): void {
    if (view.frozen) {
      this.kickoffTicks = -1;
      this.wasFrozen = true;
      return;
    }
    if (this.wasFrozen) {
      this.kickoffTicks = 0;
      this.wasFrozen = false;
      return;
    }
    if (this.kickoffTicks >= 0) this.kickoffTicks++;
  }

  /**
   * A kickoff is a state, not an event: the ball is at its spawn and at rest.
   * Testing the state rather than trusting the counter means a bot that is
   * handed a mid-play world never runs kickoff code, and one that IS on a
   * kickoff runs it even if the director was constructed mid-countdown.
   */
  private isKickoff(view: BotWorldView): boolean {
    if (this.kickoffTicks < 0 || this.kickoffTicks >= KICKOFF_MAX_TICKS) return false;
    return (
      length(view.ball.velocity) < KICKOFF_BALL_SPEED &&
      dist(view.ball.position, BALL_SPAWN) < KICKOFF_BALL_RADIUS
    );
  }

  /** Interception estimate for EVERY car, bot-driven or not. Cars without a bot
   *  are estimated with the strongest tier, so a bot never assumes a human or a
   *  foreign bot is slow. */
  private computeReaches(view: BotWorldView, prediction: BallPrediction): BallReach[] {
    const strongest = difficultyParams('allstar');
    const out: BallReach[] = [];
    for (let i = 0; i < view.cars.length; i++) {
      const slot = this.slots.find((s) => s.config.carIndex === i);
      out.push(
        estimateBallReach(view.cars[i], prediction, slot?.params ?? strongest, this.sample)
      );
    }
    return out;
  }

  private computeRoles(view: BotWorldView, reaches: BallReach[]): Map<number, Role> {
    const roles = new Map<number, Role>();
    for (const team of [0, 1] as const) {
      const candidates = [];
      for (let i = 0; i < view.cars.length; i++) {
        if (view.cars[i].team !== team) continue;
        candidates.push({
          carIndex: i,
          reachTime: reaches[i].time,
          demolished: view.cars[i].demolished,
        });
      }
      for (const [carIndex, role] of assignRoles(candidates)) roles.set(carIndex, role);
    }
    return roles;
  }

  private buildContext(
    view: BotWorldView,
    prediction: BallPrediction,
    slot: BotSlot,
    car: CarState,
    reaches: BallReach[],
    role: Role,
    ownGoalThreat: number,
    kickoffTicks: number
  ): PolicyContext {
    const carIndex = slot.config.carIndex;
    const reach = reaches[carIndex];

    let opponentReachTime = Number.POSITIVE_INFINITY;
    let teammateReachTime = Number.POSITIVE_INFINITY;
    let nearestOpponent: { carIndex: number; distance: number } | null = null;

    for (let i = 0; i < view.cars.length; i++) {
      if (i === carIndex) continue;
      const other = view.cars[i];
      if (other.demolished) continue;

      if (other.team === car.team) {
        if (reaches[i].time < teammateReachTime) teammateReachTime = reaches[i].time;
      } else {
        if (reaches[i].time < opponentReachTime) opponentReachTime = reaches[i].time;
        const d = dist(car.position, other.position);
        if (nearestOpponent === null || d < nearestOpponent.distance) {
          nearestOpponent = { carIndex: i, distance: d };
        }
      }
    }

    return {
      carPositions: this.carPositions,
      tick: view.tick,
      carIndex,
      car,
      team: car.team,
      role,
      params: slot.params,
      reach,
      opponentReachTime,
      teammateReachTime,
      ownGoalThreat,
      boostTarget: nearestBoostTarget(car, view.padActive, reach.position),
      nearestOpponent,
      ownGoal: ownGoalCenter(car.team),
      attackingGoal: attackingGoalCenter(car.team),
      view,
      prediction,
      rng: slot.rng,
      kickoffTicks,
    };
  }
}

/**
 * Seconds for `car` to reach a point, exposed here so a caller (a HUD, a
 * training harness) can use the bot's own drive model without importing the
 * decision layer.
 */
export const timeToPoint = (
  car: CarState,
  target: Vec3,
  params: DifficultyParams
): number => estimateTimeToReach(car, target, params, { arriveOffset: BALL_RADIUS });
